import { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/context';
import { getUi } from '../i18n/ui';
import { LOCALES, LOCALE_LABEL, type Locale } from '../i18n/types';
import styles from '../App.module.css';

interface OnboardingProps {
  open: boolean;
  suggestedDueDate: string;
  onComplete: (input: {
    name: string;
    locale: Locale;
    dueDate?: string | null;
    birthDate?: string | null;
    skipDates?: boolean;
  }) => void;
  onLocalePreview: (locale: Locale) => void;
  onRestoreCode: (code: string) => Promise<{ ok: boolean; message: string }>;
}

export function Onboarding({
  open,
  suggestedDueDate,
  onComplete,
  onLocalePreview,
  onRestoreCode,
}: OnboardingProps) {
  const { locale, setLocale } = useLanguage();
  const ui = getUi(locale);
  const [mode, setMode] = useState<'pregnant' | 'postpartum'>('pregnant');
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState(suggestedDueDate);
  const [birthDate, setBirthDate] = useState('');
  const [restoreCode, setRestoreCode] = useState('');
  const [restoreStatus, setRestoreStatus] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDueDate(suggestedDueDate);
  }, [open, suggestedDueDate]);

  if (!open) return null;

  function selectLocale(next: Locale) {
    setLocale(next);
    onLocalePreview(next);
  }

  return (
    <div className={styles.onboardingOverlay} role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className={styles.onboardingCard}>
        <p className={styles.onboardingEyebrow}>{ui.onboarding.eyebrow}</p>
        <h2 id="onboarding-title" className={styles.onboardingTitle}>
          {ui.onboarding.title}
        </h2>
        <p className={styles.onboardingBody}>{ui.onboarding.body}</p>

        <div className={styles.onboardingField}>
          <span>{ui.onboarding.languageLabel}</span>
          <div className={styles.langGrid} role="group" aria-label={ui.onboarding.languageLabel}>
            {LOCALES.map((code) => (
              <button
                key={code}
                type="button"
                className={`${styles.langChip} ${locale === code ? styles.langChipActive : ''}`}
                aria-pressed={locale === code}
                onClick={() => selectLocale(code)}
              >
                {LOCALE_LABEL[code]}
              </button>
            ))}
          </div>
        </div>

        <label className={styles.onboardingField}>
          <span>{ui.onboarding.nameLabel}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.onboardingInput}
            placeholder={ui.onboarding.namePlaceholder}
            autoComplete="nickname"
          />
        </label>

        <div className={styles.onboardingTabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'pregnant'}
            className={`${styles.onboardingTab} ${mode === 'pregnant' ? styles.onboardingTabActive : ''}`}
            onClick={() => setMode('pregnant')}
          >
            {ui.onboarding.pregnantTab}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'postpartum'}
            className={`${styles.onboardingTab} ${mode === 'postpartum' ? styles.onboardingTabActive : ''}`}
            onClick={() => setMode('postpartum')}
          >
            {ui.onboarding.postpartumTab}
          </button>
        </div>

        {mode === 'pregnant' ? (
          <label className={styles.onboardingField}>
            <span>{ui.onboarding.dueDateLabel}</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={styles.onboardingInput}
            />
          </label>
        ) : (
          <label className={styles.onboardingField}>
            <span>{ui.onboarding.birthDateLabel}</span>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              className={styles.onboardingInput}
            />
          </label>
        )}

        <div className={styles.onboardingActions}>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => {
              if (mode === 'pregnant' && dueDate) {
                onComplete({ name, locale, dueDate, birthDate: null });
              } else if (mode === 'postpartum' && birthDate) {
                onComplete({ name, locale, birthDate, dueDate: null });
              }
            }}
          >
            {ui.onboarding.save}
          </button>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => onComplete({ name, locale, skipDates: true })}
          >
            {ui.onboarding.skip}
          </button>
        </div>

        <div className={styles.restoreBox}>
          <p className={styles.restoreTitle}>{ui.onboarding.restoreTitle}</p>
          <p className={styles.restoreNote}>{ui.onboarding.restoreNote}</p>
          <div className={styles.restoreRow}>
            <input
              type="text"
              value={restoreCode}
              onChange={(e) => setRestoreCode(e.target.value.toUpperCase())}
              className={styles.onboardingInput}
              placeholder="ABCD-1234"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button
              type="button"
              className={styles.ghostButton}
              disabled={restoring || restoreCode.trim().length < 8}
              onClick={async () => {
                setRestoring(true);
                setRestoreStatus('');
                const result = await onRestoreCode(restoreCode.trim());
                setRestoreStatus(result.message);
                setRestoring(false);
              }}
            >
              {ui.onboarding.restoreButton}
            </button>
          </div>
          {restoreStatus ? (
            <p className={styles.shareStatus} role="status">
              {restoreStatus}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
